import {
	get_mode,
	isCamera,
	isProAnyEM,
	isRgbCct,
	isRgbwwPm,
	isShellyPresence,
} from 'src/helpers/interface_helpers';
import { doesSupportVirtualLocalActions } from './local_action_virtual_helpers';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import {
	getSwitchPeripheralDeviceByParentID,
	getSwitchPeripheralsChannel,
} from 'src/helpers/addons/addon_device_helpers';
import { devices_config } from '@devices/index';
import { isCameraLocalActionMethod } from '../devices/camera_local_actions';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';

export const isCombinationDeviceLightMode = (device: Device): boolean => {
	//ShellyPro RGBWW PM
	return (
		isRgbwwPm(device.type) &&
		/^(rgbcct|rgbx2light)$/.test(devices_settings.getByRealId(device.id)?.sys?.device?.profile)
	);
};

export const getComponentIdByDeviceChannel = (device: Device): number => {
	const deviceChannel = device.channel;
	return isCombinationDeviceLightMode(device) && deviceChannel > 0 ? deviceChannel - 1 : deviceChannel;
};

export const getComponentByDeviceMode = (
	device: Device,
): {
	cid: number;
	mode: string;
	component: string;
	deviceMode: string;
	colorTemp: {
		minValue: number;
		maxValue: number;
	};
} => {
	const reportedMode = get_mode(
		device,
		devices_status.getByRealId(device.id),
		devices_settings.getByRealId(device.id),
	);
	const deviceMode = isRgbCct(device.type) ? 'rgbcct' : reportedMode;
	if (isRgbwwPm(device.type)) {
		const cid = getComponentIdByDeviceChannel(device);
		const mode = device.mode;
		return {
			cid,
			mode,
			deviceMode,
			component: `${mode}:${cid}`,
			colorTemp: devices_config[device.type]?.modes[deviceMode]?.color_temp,
		};
	}
	const cid = device.channel;
	let mode = deviceMode;
	if (mode == 'roller') mode = 'cover';
	else if (mode == 'white') mode = 'light';
	else if (mode == 'relay') mode = 'switch';
	return {
		cid,
		mode,
		deviceMode,
		component: `${mode}:${cid}`,
		colorTemp: devices_config[device.type]?.modes[deviceMode]?.color_temp,
	};
};

export const findDeviceByChannelMode = (device: Device, job: ActionJob): boolean => {
	const method = (job?.method || '').toLowerCase();
	if (/^(SBTR-001AEU)$/.test(device?.type)) {
		return /^blutrv.(target|boost|position)$/.test(method) && device.channel - 2000 == job.params.id;
	} else if (isCombinationDeviceLightMode(device)) {
		const deviceMode = getComponentByDeviceMode(device).deviceMode;
		if (/^(rgb).set$/.test(method) && job.params?.id == 0) {
			return device.channel == 0;
		} else if (/^(rgbcct)$/.test(deviceMode) && /^(cct).set$/.test(method)) {
			return device.channel == 1;
		} else if (/^(rgbx2light)$/.test(deviceMode) && /^(light).set$/.test(method)) {
			return device.channel == (job.params?.id == 0 ? 1 : 2);
		}
	} else if (/^(S3XT-0S|virtual-group)$/.test(device?.type)) {
		if (device.component === `${method.split('.')[0]}:${job.params?.id}`) {
			return true;
		}
		return doesSupportVirtualLocalActions(device, job);
	} else if (/^(SHTRV-01)$/.test(device?.type)) {
		return /^trv.(target|boost|position|ext_t)$/.test(method);
	} else if (/^Media./i.test(method)) {
		return device.category == 'media_player';
	} else if (isCamera(device.type) && isCameraLocalActionMethod(method)) {
		return method === 'camerazone.setconfig'
			? device.channel === 0
			: job.params?.id === device.channel;
	} else if (isShellyPresence(device.type) && /^(presencezone.setconfig)$/.test(method)) {
		return Boolean(devices_settings.getByRealId(device.id)?.[`presencezone:${job.params?.id}`]);
	} else if (device?.peripheral_type === 'switch' && /^switch./i.test(method)) {
		return device.component === `switch:${job.params?.id}`;
	} else if (device.channel === 0 && getSwitchPeripheralsChannel(device.id).includes(job.params?.id)) {
		return !getSwitchPeripheralDeviceByParentID(device.id, job.params?.id);
	} else if (device.channel < 100 && !/^(boolean|enum|text|button|number)\./.test(method)) {
		return device.channel == (isProAnyEM(device.type) ? 0 : job.params?.id);
	}
	return false;
};
